"use strict";
/**
 * @author Maxim Seredkin
 */
// region imports
// angular
import angular from "angular";
import ngAnimate from "angular-animate";
import ngResource from "angular-resource";
// angular module
import ngRoute from "angular-route";
import ngSanitize from "angular-sanitize";
import uiBootstrap from "angular-ui-bootstrap";
import ngFileUpload from "ng-file-upload";
import uiSelect from "ui-select";
import swipe from "angular-swipe";
// config
import httpConfig from "./config/http";
import logConfig from "./config/log";
import routingConfig from "./config/routing";
import ngToastConfig from "./config/toast";
import uiSelectConfig from "./config/ui-select";
// controller
import AppController from "./controller/app";
import ErrorController from "./controller/error";
import HomeController from "./controller/home";
import ModalConfirmController from "./controller/modalConfirm.controller";
import MonthController from "./controller/month";
import StatusController from "./controller/status";
// filter
import CurrenciesFilter from "./filter/currencies";
import PropFilter from "./filter/prop";
import RangeFilter from "./filter/range";
// endregion
// interceptor
import errorInterceptor from "./interceptor/error";
// region module
import audit from "./module/audit";
import dashboard from "./module/dashboard";
import enchiridion from "./module/enchiridion";
import file from "./module/file";
import land from "./module/land";
import operator from "./module/operator";
import org from "./module/org";
import project from "./module/project";
import reports from "./module/reports";
import staff from "./module/staff";
import user from "./module/user";
import restServices from "./rest";
import welcome from "./module/welcome";
import support from "./module/support";
import investActivity from "./module/invest-activity";
import primaryAppeals from "./module/appeals";
import sign from './module/sign';
import investPlan from './module/invest-plan';
import projectGchp from "./module/project-gchp";

// service
import AttachmentService from "./service/attachment";
import DaDataService from "./service/daData";
import FilterUtilsService from "./service/filterUtils";
import ModalService from "./service/modal";
import Notification from "./service/notify";
import SecurityService from "./service/security";
import SettingsService from "./service/settings";
import SpinnerService from "./service/spinner";
import StatusService from "./service/status";
import UtilsService from "./service/utils";
// endregion

import CustomMultiselect from "./directives/customMultiselect";
import CustomSelect from "./directives/customSelect";
import ClickOutside from "./directives/clickOutside";
import CustomSlider from "./directives/slider";
import IconsPlace from "./directives/iconsPlace";
import { default as resizeListener, unbind } from "./resizeListener";
import phoneMaskDirective from './directives/phone-mask.directive';
import projectOptionsPlace from './directives/projectOptionsPlace';
let modules = [
  // angular modules
  ngResource,
  ngRoute,
  ngAnimate,
  ngSanitize,
  "ngToast",
  ngFileUpload,
  swipe.name,

  // vendor modules
  uiBootstrap,
  uiSelect,
  "ui.tinymce",
  "angularjs-dropdown-multiselect",
  "ui.checkbox",
  "ui.bootstrap-slider",
  "textAngular",
  "alexjoffroy.angular-loaders",
  "angularMoment",
  "rzSlider",
  'ui.swiper',

  "gantt",
  "gantt.tooltips",
  "gantt.tree",
  "gantt.resizeSensor",

  // app modules
  restServices,
  org,
  staff,
  project,
  land,
  file,
  user,
  audit,
  operator,
  enchiridion,
  reports,
  dashboard,
  welcome,
  support,
  investActivity,
  sign,
  primaryAppeals,
  investPlan,
  projectGchp,
];

angular
  .module("investmentProject", modules)
  .run([
    "amMoment",
    function (amMoment) {
      amMoment.changeLocale("ru");
    },
  ])
  .config(httpConfig)
  .config(routingConfig)
  .config(uiSelectConfig)
  .config(ngToastConfig)
  .config(logConfig)
  .filter("range", RangeFilter)
  .filter("propsFilter", PropFilter)
  .filter("currencies", CurrenciesFilter)
  .controller("AppController", AppController)
  .controller("MonthController", MonthController)
  .controller("StatusController", StatusController)
  .controller("HomeController", HomeController)
  .controller("ModalConfirmController", ModalConfirmController)
  .controller("ErrorController", ErrorController)
  .factory("StatusService", StatusService)
  .factory("ModalService", ModalService)
  .factory("AttachmentService", AttachmentService)
  .factory("$security", SecurityService)
  .factory("SettingsService", SettingsService)
  .factory("$utils", UtilsService)
  .factory("$filterUtils", FilterUtilsService)
  .factory("errorInterceptor", errorInterceptor)
  .factory("Notification", Notification)
  .factory("DaDataService", DaDataService)
  .factory("SpinnerService", SpinnerService)
  .directive("autogrow", function () {
    return function (scope, element, attr) {
      var update = function () {
        element.css("height", "auto");
        var height = element[0].scrollHeight;
        if (height > 0) {
          element.css("height", height + 2 + "px");
        }
      };
      scope.$watch(attr.ngModel, function () {
        update();
      });
      attr.$set("ngTrim", "false");
    };
  })
  .directive("infiniteScrolled", function () {
    return function (scope, elm, attr) {
      var raw = elm[0];

      elm.bind("scroll", function () {
        if (
          !scope.loading &&
          raw.scrollTop + raw.offsetHeight + 5 >= raw.scrollHeight
        ) {
          scope.$apply(attr.infiniteScrolled);
        }
      });
    };
  })
  .directive("customSelect", CustomSelect)
  .directive("clickOutside", ClickOutside)
  .directive("customMultiselect", CustomMultiselect)
  .directive("customSlider", CustomSlider)
  .directive("iconsPlace", IconsPlace)
  .directive('phoneMask', phoneMaskDirective)
  .directive("resizeListener", function() {
    return {
      scope: {
        callback: '=resizeCallback'
      },
      link: function (scope, element) {
        const raw = element[0];
        resizeListener(raw, function () {
          scope.callback();
        })
        scope.$on("$destroy", () => {
          unbind(raw);
        })
      }
    }
  })
  .directive('projectOptionsPlace', projectOptionsPlace)
  .directive('intersectionObserver', function() {
    return {
      restrict: 'A',
      scope: {
        onVisible: '=onVisible',
        item: '=callbackItem',
        isIntersected: '=isIntersected',
      },
      link: function(scope, element, attrs) {
        if (scope.isIntersected) return;
        // Создаем новый IntersectionObserver
        const observer = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              // Вызываем колбэк, если элемент видим
              scope.$apply(() => {
                scope.onVisible(scope.item);
              });
              // Отключаем observer, если нужно вызвать колбэк только один раз
              observer.unobserve(element[0]);
            }
          });
        }, {
          root: undefined, // Используем viewport как область наблюдения
          rootMargin: '0px',
          threshold: 0.1 // Порог видимости (10% элемента должно быть видно)
        });

        // Начинаем наблюдение за элементом
        observer.observe(element[0]);

        // Очистка при уничтожении директивы
        element.on('$destroy', function() {
          observer.unobserve(element[0]);
        });
      }
    };
  });
